import { supabase } from '@/lib/supabase';
import {
  DerivativeKpiMetrics,
  DerivativeKpiWithMetrics,
} from '@/shared/types/api';

export const getDerivativeKpisWithMetrics = async (
  projectId: number,
): Promise<DerivativeKpiWithMetrics[]> => {
  // Fetch derivative KPIs
  const { data: kpisData, error: kpisError } = await supabase
    .from('derivative_kpis')
    .select('*')
    .eq('project_id', projectId);

  if (kpisError) {
    throw kpisError;
  }

  if (!kpisData || kpisData.length === 0) {
    return [];
  }

  // Fetch metrics from view for all KPIs
  const kpiIds = kpisData.map((kpi) => kpi.id);
  const { data: metricsData, error: metricsError } = await supabase
    .from('derivative_kpi_metrics_view')
    .select('*')
    .in('id', kpiIds)
    .eq('project_id', projectId)
    .order('last_entry_date', { ascending: false });

  if (metricsError) {
    throw metricsError;
  }

  // Group metrics by KPI ID and get the latest one for each KPI
  const metricsByKpiId = new Map<number, DerivativeKpiMetrics>();
  (metricsData || []).forEach((metric) => {
    if (metric.id && !metricsByKpiId.has(metric.id)) {
      metricsByKpiId.set(metric.id, metric as DerivativeKpiMetrics);
    }
  });

  // Combine KPIs with their latest metrics, filtering out orphaned KPIs
  // (e.g. KPIs whose operand KPIs have been deleted)
  const derivativeKpis = kpisData
    .filter((kpi) => {
      // A derivative KPI needs at least one valid operand
      const hasValidOperand =
        kpi.kpi_operand_1 != null ||
        kpi.kpi_operand_2 != null ||
        kpi.custom_operand_1 != null ||
        kpi.custom_operand_2 != null;
      return hasValidOperand;
    })
    .map((kpi) => {
      const latestMetrics = metricsByKpiId.get(kpi.id);

      return {
        ...kpi,
        metrics: latestMetrics,
      };
    });

  return derivativeKpis as DerivativeKpiWithMetrics[];
};
